import React from 'react';
import { NodeProps, useReactFlow } from 'reactflow';
import axios from 'axios';
import { TextInput } from '../../../../form/textInput';
import { Select } from '../../../../form/select';
import { updateNodeValue } from '../../../../../services/method';
import { operationType, selectDataType } from '../../../../../services/types';
import { Checkboxs } from '../../../../form/checkboxs';
import { Main, Styled } from './style';
import { Switch } from '../../../../form/switch';
import { Textarea } from '../../../../form/textarea';
import { Pagination } from '../../../../form/pagination';
import { AuthenticateSelect } from '../../../../auth/select';
import { SettingsBlock, settingsDataType } from '../../../../settingsBlock';
import { IoCheckmarkCircleOutline, IoWarningOutline } from 'react-icons/io5';
import { ExpandedBlock } from '../../../expanded';
import { DateInput } from '../../../../form/date';

type SlackProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

export function Slack({ id, data, setChildMinimize, setChildSettings }: SlackProps) {
   const [requests, setRequests] = React.useState<selectDataType[]>([
      { id: 1, name: 'Fetch Attachments', refId: 'fetch_attachments', isActive: true }, 
      { id: 2, name: 'Upload File', refId: 'upload_file' }, { id: 3, name: 'Create Text File', refId: 'create_text_file' },
      { id: 4, name: 'Delete File', refId: 'delete_file' },
      { id: 5, name: 'Join Channel', refId: 'join_channel' }, { id: 6, name: 'Get Messages', refId: 'get_messages' },
      { id: 7, name: 'Search Messages', refId: 'search_messages' }, { id: 8, name: 'Create Message', refId: 'create_message' },
      { id: 9, name: 'Update Message', refId: 'update_message' }, { id: 10, name: 'Delete Message', refId: 'delete_message' },
      { id: 11, name: 'Let AI Agent Decide', refId: 'agent_decide' },
   ]);
   const { setNodes } = useReactFlow();
   const credential = { headers: { Authorization: `Bearer ${data.value?.connection?.token }` }};
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);
   
   // --
   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // ---
   React.useEffect(() => {
      if (!data.value) {
         if (data.currentCategory === 'Actions') {
            setRequests(requests => requests.map(request => ({ ...request, isActive: request.refId === 'create_message' })));

            setNodes(nodes => nodes.map(node => {
               if (node.id === id) {
                  return { ...node, data: { ...node.data, value: { ...node.data.value, 
                     request: 'create_message', channels: [], accountType: 'bot'
                  }}}
               } else return node;
            }));
         } else updateNodeValue(id, setNodes, { request: requests[0].refId });
      } else {
         setRequests(requests => requests.map(request => ({ ...request, isActive: request.refId === data.value.request })));
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // ---
   const fetchChannels = async (token: string) => {
      const channels = (await axios.get('/social_actions/slack/list_channels', { headers: { Authorization: `Bearer ${token}` }})).data.channels;
      updateNodeValue(id, setNodes, { channels: channels.map((item: any, index: number) => ({ id: item.id, name: item.name, isActive: index === 0 })) });
   }

   // ---
   const handleAuthenticate = async (type: 'add' | 'remove', data: { id: string, token: string }) => {
      if (type === 'add') {
         updateNodeValue(id, setNodes, { connection: data, connectionId: data?.id || null });
         await fetchChannels(data.token);
      } else {
         updateNodeValue(id, setNodes, { connection: null });
      }
   }

   // Fetch channels whenever a connection is present but none have been loaded yet.
   // Not gated on isExpanded: a connection can be assigned from the Global Connections
   // popup while the node is collapsed, and it must fully initialize immediately.
   React.useEffect(() => {
      if (data.value?.connection?.token && !data.value?.channels?.length) {
         fetchChannels(data.value.connection.token);
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data.value?.connection]);
   // ---
   const handleChangeInput = (e: any, keyRef?: string) => {
      if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: typeof e === 'string' ? e : e.target.value });
   }

   // ---
   const handleSelectRequests = (action: 'request' | 'channels', selectId: number) => {
      if (action === 'request') {
         const refId = requests.find(obj => obj.id === selectId)?.refId;

         setRequests((requests) => requests.map((request) => {
            if (request.id === selectId) return { ...request, isActive: true };
            else return { ...request, isActive: false };
         }));
         setNodes(nodes => nodes.map(node => {
            if (node.id === id) {
               return { ...node, data: { ...node.data, value: { ...node.data.value, request: refId }}}
            } else return node;
         }));

      } else if (action === 'channels') {
         const arr = data.value[action].map((item: any) => ({ ...item, isActive: item.id === selectId }));
         updateNodeValue(id, setNodes, { [action]: arr });
      }
   }

   // ---
   const handleButtons = async (type: 'attachments' | 'page' | 'download', value?: any) => {
      try {
         if (type === 'attachments' || type === 'page') {
            updateNodeValue(id, setNodes, { isLoading: true });
            const channelId = data.value.channels.find((item: any) => item.isActive).id;
            const response = (await axios.get(`/social_actions/slack/list_files?channel=${channelId}&page=${type === 'page' ? value : 1}`, credential)).data;
            updateNodeValue(id, setNodes, { 
               isLoading: false, attachments: response.files.map((file: any) => ({ id: file.id, name: file.name })),
               page: { channelId, current_page: response.page, total_pages: response.total_pages }
            });

         } else if (type === 'download') {
            const filter = data.value.attachments.filter((file: any) => file.isActive && !file.url).map((file: any) => file.id);
      
            if (filter.length) {
               updateNodeValue(id, setNodes, { isLoading: true });
               const files: { id: number, url: string, type: string }[] = [];
               
               for (const id of filter) {
                  const response = (await axios.get(`/social_actions/slack/download_file?file_id=${id}`, credential)).data;
                  files.push({ id, url: response.url, type: response.mime_type });
               }
               const reMap = data.value.attachments.map((file: any) => ({ 
                  ...file, 
                  url: file.url || files.find((f) => f.id === file.id)?.url || '',
                  type: file.type || files.find((f) => f.id === file.id)?.type || '',
               }));
               updateNodeValue(id, setNodes, { isLoading: false, attachments: reMap });
            }
         }
      } catch {
         updateNodeValue(id, setNodes, { isLoading: false });
      }
   }

   // ---
   const attachmentsLengthActive = data.value?.request === 'fetch_attachments' ? data.value?.attachments?.filter((item: any) => item.isActive)?.length : 0;
   const isWarning = !data.value?.connection;

   return (<>
      <SettingsBlock nodeId={ id }data={ settings } setData={ setSettings } />
      <Main>
         {isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={ 21 } /></span>
         </> : null}
         { isExpanded ? <ExpandedBlock
            data={{ id, ...data }} 
            handleClose={ () => setIsExpanded(false) } 
            isWarning={ isWarning }
            styleParent={ (data.value?.request === 'fetch_attachments' && data.value?.attachments?.length ? { right: '-136.5px' } : {}) }
            style={{ minWidth: '430px' }}
         ><Styled>
            <AuthenticateSelect type="slack" defaultId={ data.value?.connectionId || data.value?.connection?._id || data.value?.connection?.id || '' } defaultConnection={ data.value?.connection } handleAuthenticate={ handleAuthenticate } background={ data.background } />
            <div className="action">
               { data.value?.connection && data.value?.request === 'fetch_attachments' ? <>
                  <button className="btn1 withBorder" onClick={ _=> handleButtons('attachments') } disabled={ data.value?.isLoading }>Fetch</button> 
                  { attachmentsLengthActive ? <button className="btn1 withBorder download" onClick={ _=> handleButtons('download') } disabled={ data.value?.isLoading }>Download</button> : null }
               </> : null }
            </div>
            <Select title="Request" data={ requests } handleSelectItem={ (id) => handleSelectRequests('request', id) } isSmall={ true } />
            { data.value?.request !== 'search_messages' && data.value?.request !== 'delete_file' ? <Select title="Channel Id" data={ data.value?.channels } handleSelectItem={ (id) => handleSelectRequests('channels', id) } isSmall={ true } /> : null }
            { data.value?.request === 'create_text_file' ? <>
               <TextInput keyRef="filename" value={ data.value?.filename } title="Filename" placeholder="file_name" handleChange={ handleChangeInput } /> 
               <TextInput keyRef="title" value={ data.value?.title } title="Title" placeholder="file_title" handleChange={ handleChangeInput } /> 
               <TextInput keyRef="initial_comment" value={ data.value?.initial_comment } title="Comment" placeholder="initial_comment" handleChange={ handleChangeInput } /> 
            </> : null }
            { data.value?.request === 'get_messages' ? <>
               <TextInput keyRef="limit" value={ data.value?.limit } title="Limit" placeholder="messages_limit" handleChange={ handleChangeInput } />
               <DateInput keyRef="startTime" title="Start Time" isWithTime={ true } value={ data.value?.startTime || '' } handleChange={ handleChangeInput } />
               <DateInput keyRef="endTime" title="End Time" isWithTime={ true } value={ data.value?.endTime || '' } handleChange={ handleChangeInput } />
            </> : null }
            { ['update_message', 'delete_message'].includes(data.value?.request) ? 
               <TextInput keyRef="message_ts" value={ data.value?.message_ts } title="TS" placeholder="message_ts" handleChange={ handleChangeInput } /> 
            : null }
            { data.value?.request === 'search_messages' ? <TextInput keyRef="query" value={ data.value?.query } title="Query" placeholder="query" handleChange={ handleChangeInput } /> : null }
            { ['create_text_file', 'create_message', 'update_message'].includes(data.value?.request) ? 
               <Textarea keyRef="any" value={ data.value?.any } placeholder="Content" handleChange={ handleChangeInput } /> 
            : null }
            { data.value?.request === 'delete_file' ? <TextInput keyRef="file_id" value={ data.value?.file_id } title="File Id" placeholder="file_id" handleChange={ handleChangeInput } /> : null }
            { data.value?.request === 'fetch_attachments' ? <>
               { data.value?.attachments?.length ? <div className="filesEdit">
                  <h3 className="title" style={{ backgroundColor: data.background, color: '#fff' }}>Attachments ({ attachmentsLengthActive }/{ data.value.attachments.length })</h3>
                  <div className="scroll nowheel">
                     <Pagination 
                        disabled={ data.value?.isLoading }
                        currentPage={ data.value.page?.current_page } 
                        totalPages={ data.value.page?.total_pages } 
                        onPageChange={ (number) => handleButtons('page', number) } 
                        color={ data.background }
                     />
                     <Checkboxs 
                        data={ data.value.attachments.map((file: any) => ({ ...file, isNotActive: !file.url })) } 
                        handelCheckbox={ (fileId: any, value?: boolean) => { 
                           updateNodeValue(id, setNodes, { attachments: data.value.attachments.map((item: any) => {
                              if (item.id === fileId) return ({ ...item, isActive: !value })
                              else return item;
                           })});
                        }} 
                     />
                  </div>
               </div> : null }
               <Switch title="Start autopilot (with loop)" className="autopilot" value={ !!data.value?.isAutopilot } setValue={ () => updateNodeValue(id, setNodes, { isAutopilot: !data.value?.isAutopilot }) } />
               <p>Please select your keywords carefully to avoid downloading incorrect or unintended files!</p> 
            </>: null}
         </Styled></ExpandedBlock> : null}
      </Main>
   </>);
}